import React, { memo, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import _debounce from "lodash/debounce";
import {
  RangeDropdown,
  ExcelBtn,
  SearchBar,
  Pagination,
  NoRecordFound,
} from "../../component/commonComponent";
import doubleArrow from "../../assets/admin Images/2arrows.svg";
import { PageSize } from "../../utils/constant";
import { exportToCSV, toastMessage } from "../../utils/helper";
import userAuth from "../../utils/api/userAuth";
import moment from "moment";
import CSATSurveyFilterBtn from "../../component/commonComponent/CSATSurveyFilterBtn";
import TIMER_ICON from "../../assets/images/clock-filter.svg";
import { Info } from "lucide-react";
import CandidateCSATOverview from "./CandidateCSATOverview";

const headerOptions = [
  {
    name: "Employer Name",
    sortBy: "employerName",
    width: "w-15",
  },
  {
    name: "Candidate Name",
    sortBy: "candidateName",
    width: "w-15",
  },
  {
    name: "Candidate Email",
    sortBy: "candidateEmail",
    width: "w-18",
  },
  {
    name: "Product Name",
    sortBy: "",
    width: "w-15",
  },
  {
    name: "Overall Experience (Rating)",
    sortBy: "",
    width: "w-15",
  },
  {
    name: "Question Clarity",
    sortBy: "",
    width: "w-10",
  },
  {
    name: "AI vs Human comparison",
    sortBy: "",
    width: "w-15",
  },
  {
    name: "Additional Comments",
    sortBy: "",
    width: "w-15",
  },
  {
    name: "Submission Date & Time",
    sortBy: "",
    width: "w-15",
  },
];

const defaultFilters = {
  from: "",
  to: "",
  aiVsHumanRating: "",
  rating: "",
  interviewId: "",
  positionId: "",
  productId: "",
  interviewCode: "",
};

const getTrimmedText = (text = "", maxLength = 20) => {
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

const tooltipStyle = {
  maxWidth: "260px",
  whiteSpace: "normal",
  wordWrap: "break-word",
  userSelect: "text",
  zIndex: 999999,
  padding: "8px",
};

const CandidateCSAT = memo(() => {
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesLimit, setEntriesLimit] = useState(PageSize);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [isAsc, setIsAsc] = useState(true);
  const [surveyFeedbackData, setSurveyFeedbackData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFiltersState] = useState({ ...defaultFilters });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isScreenSideSectionOpen, setIsScreenSideSectionOpen] = useState(false);
  const [sideSectionScreenData, setSideSectionScreenData] = useState(null);
  const [sideSectionScreenId, setSideSectionScreenId] = useState(null);

  const handleEntriesLimit = (limit) => {
    setEntriesLimit(limit);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleChangeSort = (key) => {
    if (sortBy === key) {
      setIsAsc(!isAsc);
    } else {
      setSortBy(key);
      setIsAsc(true);
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const applyFilters = (nextFilters) => {
    setCurrentPage(1);
    const merged = { ...defaultFilters, ...nextFilters };

    setFiltersState(merged);
  };

  const filtersForControl = useMemo(() => {
    return {
      from: filters.from ? moment(filters.from).format("YYYY-MM-DD") : "",
      to: filters.to ? moment(filters.to).format("YYYY-MM-DD") : "",
      aiVsHumanRating: filters.aiVsHumanRating || "",
      rating:
        filters.rating === null || filters.rating === undefined
          ? ""
          : String(filters.rating),
      interviewId: filters.interviewId || "",
      positionId: filters.positionId || "all",
      productId: filters.productId || "",
      interviewCode: filters.interviewCode || "",
    };
  }, [filters]);

  useEffect(() => {
    const fetchPlatformSurvey = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("pageSize", String(entriesLimit));

        if (filters.from) {
          params.set("from", filters.from);
        }

        if (filters.to) {
          params.set("to", filters.to);
        }

        if (filters.aiVsHumanRating) {
          params.set("aiVsHumanRating", filters.aiVsHumanRating);
        }

        const normalizedRating =
          filters.rating === null ||
          filters.rating === undefined ||
          String(filters.rating).trim() === ""
            ? ""
            : String(filters.rating).trim();

        if (normalizedRating) {
          params.set("rating", normalizedRating);
        }

        if (filters.productId && String(filters.productId).trim()) {
          params.set("productId", String(filters.productId).trim());
        }

        if (search.trim()) {
          params.set("search", search.trim());
        }

        if (sortBy) {
          const direction = isAsc ? "asc" : "desc";
          params.set("sort", `${sortBy} ${direction}`);
        }

        const queryString = params
          .toString()
          .replace(/%3A/gi, ":")
          .replace(/%2C/gi, ",");
        const endpoint = queryString
          ? `/admin/users/csat/feedback/responses?${queryString}`
          : "/admin/users/csat/feedback/responses";

        const response = await userAuth.get(endpoint);
        const apiData = response?.data?.result?.data || [];
        setSurveyFeedbackData(apiData);
        // Extract total count from API response - check multiple possible locations
        const total = response?.data?.result?.total || apiData.length;
        setTotalCount(total);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to load interview survey data.";
        toastMessage("error", message);
        setSurveyFeedbackData([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlatformSurvey();
  }, [
    currentPage,
    entriesLimit,
    filters.from,
    filters.to,
    filters.aiVsHumanRating,
    filters.rating,
    filters.interviewId,
    filters.positionId,
    filters.productId,
    search,
    sortBy,
    isAsc,
    refreshTrigger,
  ]);

  useEffect(() => {
    if (isScreenSideSectionOpen && sideSectionScreenId) {
      const fetchScreenData = async () => {
        try {
          const { data } = await userAuth.get(
            `/admin/users/csat/screen/${sideSectionScreenId}`,
          );
          // console.log("screen data?.result:", data?.result);

          setSideSectionScreenData(data?.result);
        } catch (error) {
          console.error("Failed to fetch screen data:", error);
        }
      };
      fetchScreenData();
    }
  }, [isScreenSideSectionOpen]);

  const handleRefresh = _debounce(() => {
    setSortBy("");
    setSearch("");
    setCurrentPage(1);
    applyFilters(defaultFilters);
    setRefreshTrigger((prev) => prev + 1);
  }, 300);

  const surveyDataForCsv = useMemo(() => {
    return surveyFeedbackData.map((item) => ({
      "Employer Name": item.employerName,
      "Candidate Name": item.candidateName,
      "Candidate Email": item.candidateEmail,
      "Product Name": item.productName,
      "Overall Experience": item.overallExperience?.score ?? "",
      "Question Clarity": item.questionClarity?.score ?? "",
      "AI vs Human comparison": item.aiVsHumanComparison?.value ?? "",
      "Additional Comments": item.additionalComments,
      "Submitted on - Date": moment(item.submittedAt).format("Do MMM, YYYY"),
      "Submitted on - Time": moment(item.submittedAt).format("h:mm A"),
    }));
  }, [surveyFeedbackData]);

  return (
    <>
      <div className="page-container eep">
        <div className="mpl-title-container d-flex justify-content-between align-items-center rounded-3">
          <div className="text-theme-blue f-sem-bol f-20">
            Candidate Interview Survey {`(${totalCount})`}
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <RangeDropdown handleEntriesLimit={handleEntriesLimit} />
          <div className="d-flex align-items-center">
            <SearchBar
              onChange={handleSearch}
              value={search}
              containerClass={"search-bar-container-type1"}
              inputClass={"search-input-type1"}
            />
            <img
              src={TIMER_ICON}
              alt="timer"
              className="refresh-btn"
              onClick={handleRefresh}
            />
            <CSATSurveyFilterBtn
              setFilters={applyFilters}
              activeFilters={filtersForControl}
            />
            {surveyFeedbackData.length > 0 ? (
              <ExcelBtn
                onClick={_debounce(
                  () =>
                    exportToCSV(
                      surveyDataForCsv,
                      "Candidate Interview Survey List",
                    ),
                  300,
                )}
              />
            ) : (
              <ExcelBtn excelBtnClassName="excel-grey-btn cursor-na" />
            )}
          </div>
        </div>
        <div className="sp-list">
          <div className="header d-flex f-12 f-sem-bol rounded-3">
            {headerOptions.map((data, index) => (
              <div key={index} className={`header-content ${data.width}`}>
                <span>{data.name}</span>
                {data.sortBy && (
                  <img
                    src={doubleArrow}
                    alt="double"
                    className="img-fluid"
                    onClick={() => handleChangeSort(data.sortBy)}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="row-container">
            {isLoading ? (
              <div className="text-center py-3">Loading...</div>
            ) : surveyFeedbackData.length > 0 ? (
              surveyFeedbackData.map((item, index) => (
                <div
                  className="support-row d-flex f-12 f-sem-bol rounded-3"
                  key={index}
                >
                  <div className="row-content w-15">
                    <span className="t-clip w-100 text-center">
                      {item.employerName}
                    </span>
                  </div>
                  <div
                    className="row-content w-15"
                    onClick={() => {
                      setSideSectionScreenId(item.interviewId);
                      setIsScreenSideSectionOpen(!isScreenSideSectionOpen);
                    }}
                  >
                    <span className="blue_link_text">{item.candidateName}</span>
                  </div>
                  <div className="row-content w-18">
                    <span className="t-clip w-100 text-center">
                      {item.candidateEmail}
                    </span>
                  </div>
                  <div className="row-content w-15">
                    <span className="t-clip w-100 text-center">
                      {item.productName}
                    </span>
                  </div>
                  <div className="row-content w-15">
                    <span className="t-clip w-100 text-center service-satisfaction-container">
                      {`${item.overallExperience?.score ?? 0}/5`}
                      {item.overallExperience?.comment && (
                        <>
                          <Info
                            size={12}
                            data-tooltip-id={`overall-comment-tooltip-${index}`}
                            data-tooltip-content={
                              item.overallExperience.comment
                            }
                            data-tooltip-place="top"
                            style={{ cursor: "pointer" }}
                          />
                          <Tooltip
                            id={`overall-comment-tooltip-${index}`}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="row-content w-10">
                    <span className="t-clip w-100 text-center service-satisfaction-container">
                      {`${item.questionClarity?.score ?? 0}/5`}
                      {item.questionClarity?.comment && (
                        <>
                          <Info
                            size={12}
                            data-tooltip-id={`evaluation-comment-tooltip-${index}`}
                            data-tooltip-content={item.questionClarity.comment}
                            data-tooltip-place="top"
                            style={{ cursor: "pointer" }}
                          />
                          <Tooltip
                            id={`evaluation-comment-tooltip-${index}`}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="row-content w-15">
                    <span className="t-clip w-100 text-center service-satisfaction-container">
                      {`${item.aiVsHumanComparison?.value || "-"}`}
                      {item.aiVsHumanComparison?.comment && (
                        <>
                          <Info
                            size={12}
                            data-tooltip-id={`scheduling-comment-tooltip-${index}`}
                            data-tooltip-content={
                              item.aiVsHumanComparison.comment
                            }
                            data-tooltip-place="top"
                            style={{ cursor: "pointer" }}
                          />
                          <Tooltip
                            id={`scheduling-comment-tooltip-${index}`}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        </>
                      )}
                    </span>
                  </div>
                  <div className="row-content w-15">
                    <span
                      className="t-clip w-100 text-center"
                      data-tooltip-id={`suggestions-tooltip-${index}`}
                      data-tooltip-content={item.additionalComments}
                      data-tooltip-place="top"
                    >
                      {item.additionalComments
                        ? getTrimmedText(item.additionalComments)
                        : "-"}
                    </span>
                    <Tooltip
                      id={`suggestions-tooltip-${index}`}
                      className="tooltip-custom"
                      positionStrategy="fixed"
                      style={tooltipStyle}
                    />
                  </div>
                  <div className="row-content w-15">
                    <span className="t-clip w-100 text-center">
                      {moment(item.submittedAt).format("Do MMM, YYYY")}
                      <br />
                      {moment(item.submittedAt).format("h:mm A")}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <NoRecordFound />
            )}
          </div>
        </div>
        {surveyFeedbackData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={entriesLimit}
            onPageChange={handlePageChange}
          />
        )}

        {isScreenSideSectionOpen && sideSectionScreenData && (
          <CandidateCSATOverview
            isScreenSideSectionOpen={isScreenSideSectionOpen}
            setIsScreenSideSectionOpen={setIsScreenSideSectionOpen}
            sideSectionScreenData={sideSectionScreenData}
            setSideSectionScreenData={setSideSectionScreenData}
          />
        )}
      </div>
    </>
  );
});

export default CandidateCSAT;
